import React, {useRef, useState} from 'react';

import Summary from '@Themes/natucare/modules/Article/components/Calculator/components/Summary/Summary';
import Spacer from '@Themes/natucare/modules/Article/components/Calculator/components/Spacer/Spacer';
import TilesList from '@Themes/natucare/modules/Article/components/Calculator/components/TilesList/TilesList';
import Hr from '@Themes/natucare/modules/Article/components/Calculator/components/Hr/Hr';
import Cta from '@Themes/natucare/modules/Article/components/Calculator/components/Cta/Cta';
import Calculator from '@Themes/natucare/modules/Article/components/Calculator/Calculator';
import CalculatorForm
  from '@Themes/natucare/modules/Article/components/Calculator/modules/CalculatorForm/CalculatorForm';
import CalculatorSubscribe
  from '@Themes/natucare/modules/Article/components/Calculator/modules/CalculatorSubscribe/CalculatorSubscribe';

import addTagsCustomer from './utils/addTagsCustomer';
import getUserId from './utils/getUserId';
import addUserToNewsletter from './utils/addUserToNewsletter';
import sendEmail from "./utils/sendEmail";

import {CALCULATOR_DATA} from './constants/calculatorData';
import {CALCULATOR_TAGS} from './constants/calculatorTags';
import {CALCULATOR_ERRORS} from './constants/calculatorErrors';

import * as styles from './CalculatorApi.module.scss';

const CalculatorApi = ({data, locale = 'intl' }) => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    age: 0,
    gender: ''
  });
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState({
    radio: false,
    input: {
      0: false,
      1: false,
      2: false
    }
  });
  const [step, setStep] = useState(1);
  const [isSending, setIsSending] = useState(false);

  const handleStepChange = event => {
    event.preventDefault();
    const { target } = event;
    const formData = new FormData(target);

    setFormData({
      age: formData.get('age') < 25 ? 25 : formData.get('age') > 120 ? 120 : formData.get('age'),
      gender: formData.get('gender')?.length > 0 ? formData.get('gender') : 'women'
    })

    if (formData.get('age')?.length > 0 && formData.get('weight')?.length > 0 && formData.get('height')?.length > 0 && formData.get('gender')?.length > 0) {
      setError(false);
      setStep(prevState => prevState + 1);
    } else {
      setError(true);
      setErrors({
        radio: target.gender.value === '',
        input: {
          0: formData.get('age')?.length === 0,
          1: formData.get('weight')?.length === 0,
          2: formData.get('height')?.length === 0
        }
      })
    }
  };

  const tagsToAdd = [CALCULATOR_TAGS.CALCULATOR_COLLAGEN_LIST];

  const handleSubscribe = async (event) => {
    event.preventDefault();
    const email = formRef.current?.email?.value;

    setIsSending(true);

    if ((typeof(email) === 'string') && email.length > 0) {
      const data = await addUserToNewsletter({email, tags: tagsToAdd});
      const customerId = data.errorCode === CALCULATOR_ERRORS.API_ERROR
        ? await getUserId({email})
        : data?.data?.id;
      if (customerId) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({'event': 'subscribe_nwl'});
        
        addTagsCustomer({id: customerId, tags: tagsToAdd}).then(
          response => {
            if (response.message === 'Successfully added tags!') {
              const percent = CALCULATOR_DATA.data[formData.age][formData.gender] || '100%';
              const male = formData.gender === 'man';

               sendEmail({
                email,
                percent,
                male,
                locale
               });

               setError(false);
               setStep(prevState => prevState + 1);
            }
          }
        )
      }
    } else {
      setError(true);
      setIsSending(false);
    }
  };

  const renderComponent = step => {
    switch (step) {
      case 1:
        return <CalculatorForm
          {...data.step1}
          handleSubmit={handleStepChange}
          error={errors}
          errorMsg={error ? CALCULATOR_ERRORS.FIRST_STEP_ERROR : null} />
      case 2:
        return <CalculatorSubscribe
          ref={formRef}
          {...data.step2}
          infoMsg={CALCULATOR_DATA.texts.step2.infoMsg}
          button={{
            label: !isSending ? data.step2.button.label : '',
            icon: isSending ? 'loading' : '',
            disabled: isSending
          }}
          handleSubmit={handleSubscribe}
          errorMsg={error ? CALCULATOR_ERRORS.SUBSCRIBE_ERROR : null} />
      case 3:
        return <div className={styles.rootSummary}>
          {data.step3?.summary && <>
            <Summary
              {...data.step3?.summary}
              percent={CALCULATOR_DATA.data[formData.age][formData.gender]}
              icon={CALCULATOR_DATA.icons[formData.gender]} />
          </>}
          {data.step3?.lists[0] && <>
            <Spacer variant='md'/>
            <TilesList
              external
              heading={data.step3?.lists[0].heading}
              items={data.productsToDisplay} />
          </>}
          {data.step3?.lists[0] && <>
            <Spacer variant='md'/>
            <TilesList
              heading={data.step3.lists[1].heading}
              items={data.postsToDisplay?.guide} />
            <Spacer variant='md'/>
            <Hr />
          </>}
          {data.step3?.cta && <>
            <Spacer variant='md'/>
            <Cta {...data.step3.cta} />
          </>}
          {data.step3?.lists[2] && <>
            <Spacer variant='md'/>
            <TilesList
              heading={data.step3.lists[2].heading}
              items={data.postsToDisplay?.others} />
          </>}
        </div>;
      default:
        return null;
    }
  };

  return (
    <Calculator heading={data.heading} description={data.description} badge={data.badge}>
      {renderComponent(step)}
    </Calculator>
  );
};

export default CalculatorApi;
